import React from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../constants/api';
import dayjs from 'dayjs';
import { Table, Button, Modal, Form, Input, Select, Space, Popconfirm, InputNumber, Row, Col, DatePicker } from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';

const { Option } = Select;

export const VoucherTable = ({ dataSource, loading, onEdit, onDelete }) => {
  const [currencies, setCurrencies] = React.useState([]);

  React.useEffect(() => {
    axios.get(`${API_BASE_URL}General/GetSupportedCurrencies`)
      .then(response => {
        setCurrencies(response.data || []);
      })
      .catch(error => console.error('Error fetching currencies:', error));
  }, []);

  const getSymbol = (currency) => {
    if (!currency) return '';
    const curStr = currency.toString();
    const cur = currencies.find(c => c.x.toString() === curStr);
    return cur ? cur.sympol : '';
  };

  const columns = [
    {
      title: 'معرف',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: 'الاسم',
      key: 'accountName',
      render: (_, record) => {
        if (record.type === 1) {
          return record.creditAccountName || '';
        } else if (record.type === -1) {
          return record.debitAccountName || '';
        }
        return '';
      },
    },
    {
      title: 'تاريخ',
      dataIndex: 'voucherDate',
      key: 'voucherDate',
      render: (date) => date ? new Date(date).toLocaleDateString() : '',
    },
    {
      title: 'قيمة',
      dataIndex: 'value',
      key: 'value',
    },
    {
      title: 'العملة',
      dataIndex: 'currency',
      key: 'currency',
      render: (currency) => getSymbol(currency),
    },
    {
      title: 'ملاحظات',
      dataIndex: 'notes',
      key: 'notes',
    },
    {
      title: 'إجراءات',
      key: 'actions',
      render: (text, record) => (
        <Space size="middle">
          <Popconfirm
            title="هل أنت متأكد من حذف هذا القيد؟"
            onConfirm={() => onDelete(record.id)}
            okText="نعم"
            cancelText="لا"
          >
            <Button type="link" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="table-responsive-wrapper">
      <Table
        columns={columns}
        dataSource={dataSource}
        rowKey="id"
        loading={loading}
        scroll={{ x: 'max-content' }}
      />
    </div>
  );
};

export const VoucherModal = ({ isVisible, onOk, onCancel, editingVoucher, form, accounts, fixedType }) => {
  const [currencies, setCurrencies] = React.useState([]);
  

  React.useEffect(() => {
    if (isVisible) {
      axios.get(`${API_BASE_URL}General/GetSupportedCurrencies`)
        .then(response => {
          setCurrencies(response.data || []);
        })
        .catch(error => console.error('Error fetching currencies:', error));
    }
  }, [isVisible]);

  React.useEffect(() => {
    if (isVisible && editingVoucher) {
      form.setFieldsValue({
        ...editingVoucher,
        VoucherDate: editingVoucher.voucherDate ? dayjs(editingVoucher.voucherDate) : null,
        Currency: editingVoucher.currency || 1,
        Type: fixedType,
      });
    } else if (isVisible && fixedType !== undefined) {
      form.setFieldsValue({
        Type: fixedType,
        Currency: 1,
        VoucherDate: dayjs(),
      });
      if (fixedType === 1) {
        form.setFieldsValue({ DebitAccountId: 111 });
      } else if (fixedType === -1) {
        form.setFieldsValue({ CreditAccountId: 111 });
      }
    }
  }, [isVisible, editingVoucher, fixedType, form]);

  return (
    <Modal
      title={editingVoucher ? 'تعديل' : 'إضافة'}
      open={isVisible}
      onOk={onOk}
      onCancel={onCancel}
      width="90%"
      style={{ maxWidth: 600 }}
    >
      <Form form={form} layout="vertical">
        <Row gutter={[8,8]}>

            <Col xs={24} sm={12}>
              <Form.Item
                name="DebitAccountId"
                label="حساب المستفيد"
                rules={fixedType !== 1 ? [{ required: true, message: 'يرجى اختيار حساب المستفيد!' }] : []}
                style={fixedType === 1 ? { display: 'none' } : undefined}
              >
                <Select placeholder="اختر حساب المستفيد">
                  {accounts.map(account => (
                    <Option key={account.id} value={account.id}>{account.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item
                name="CreditAccountId"
                label="حساب المسدد"
                rules={fixedType !== -1 ? [{ required: true, message: 'يرجى اختيار حساب المسدد!' }] : []}
                style={fixedType === -1 ? { display: 'none' } : undefined}
              >
                <Select placeholder="اختر حساب المسدد">
                  {accounts.map(account => (
                    <Option key={account.id} value={account.id}>{account.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>


          <Col xs={24} sm={8}>
            <Form.Item
              name="DateTime"
              label="تاريخ القيد"
              rules={[{ required: true, message: 'يرجى اختيار التاريخ!' }]}
            >
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item
              name="Value"
              label="القيمة"
              rules={[{ required: true, message: 'يرجى إدخال القيمة!' }]}
            >
              <InputNumber min={0} style={{ width: '100%' }}/>
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item
              name="Currency"
              label="العملة"
              rules={[{ required: true, message: 'يرجى اختيار العملة!' }]}
            >
              <Select placeholder="اختر العملة">
                {currencies.map(c => (
                  <Option key={c.x} value={c.x}>{c.Sympol || c.sympol}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Form.Item
          name="Notes"
          label="ملاحظات"
        >
          <Input.TextArea />
        </Form.Item>
      </Form>
    </Modal>
  );
};
